/**
 * Public Search Service
 * Handles business search with fuzzy matching and analytics
 */

import { supabase } from '../config/supabase';

export interface SearchResult {
  id: string; // Will be converted from integer to string
  store_name: string;
  description: string;
  location: string;
  avatar_url: string | null;
  business_type: 'trader' | 'craftie' | 'hospitality' | 'professional';
  keywords: string[];
  relevance_score: number;
  similarity_score: number;
  match_type: 'exact_name' | 'exact_description' | 'keyword_match' | 'fulltext_match' | 'fuzzy_match';
  slug?: string;
}

export interface SearchParams {
  query: string;
  location?: string;
  businessType?: 'trader' | 'craftie' | 'hospitality' | 'professional';
  limit?: number;
  similarityThreshold?: number;
}

export interface SearchResponse {
  results: SearchResult[];
  totalCount: number;
  hasExactMatches: boolean;
  hasFuzzyMatches: boolean;
  searchTime: number;
}

const hydrateTraderSlugs = async (results: SearchResult[]): Promise<SearchResult[]> => {
  const traderIds = [...new Set(
    results
      .filter(result => result.business_type === 'trader' && !result.slug)
      .map(result => result.id)
  )];

  if (traderIds.length === 0) {
    return results;
  }

  const { data, error } = await supabase
    .from('traders')
    .select('id, slug')
    .in('id', traderIds);

  if (error) {
    console.error('Failed to hydrate trader slugs:', error);
    return results;
  }

  const slugMap = new Map<string, string>();
  data?.forEach(trader => {
    if (trader.slug) {
      slugMap.set(String(trader.id), trader.slug);
    }
  });

  return results.map(result => {
    if (result.business_type !== 'trader' || result.slug) {
      return result;
    }

    const slug = slugMap.get(result.id);
    return slug ? { ...result, slug } : result;
  });
};

const hydrateCraftieSlugs = async (results: SearchResult[]): Promise<SearchResult[]> => {
  const craftieIds = [...new Set(
    results
      .filter(result => result.business_type === 'craftie' && !result.slug)
      .map(result => result.id)
  )];

  if (craftieIds.length === 0) {
    return results;
  }

  const { data, error } = await supabase
    .from('crafties')
    .select('id, slug')
    .in('id', craftieIds);

  if (error) {
    console.error('Failed to hydrate craftie slugs:', error);
    return results;
  }

  const slugMap = new Map<string, string>();
  data?.forEach(craftie => {
    if (craftie.slug) {
      slugMap.set(String(craftie.id), craftie.slug);
    }
  });

  return results.map(result => {
    if (result.business_type !== 'craftie' || result.slug) {
      return result;
    }

    const slug = slugMap.get(result.id);
    return slug ? { ...result, slug } : result;
  });
};

/**
 * Fallback search using simple ILIKE and keyword matching
 */
const searchBusinessesFallback = async (params: SearchParams): Promise<SearchResponse> => {
  const startTime = performance.now();
  
  try {
    const { query, location, businessType, limit = 50 } = params;
    console.log('🔄 Using fallback search:', { query, location, businessType });

    const searchQuery = query.toLowerCase().trim();
    const scanLimitPerType = Math.max(500, limit * 10);
    const results: SearchResult[] = [];

    // Search traders - include verified OR mockup entries
    if (!businessType || businessType === 'trader') {
      const { data: traders } = await supabase
        .from('traders')
        .select('id, store_name, description, location, avatar_url, keywords, slug')
        .or('verified.eq.true,mockup.eq.true')
        .limit(scanLimitPerType);

      traders?.forEach(trader => {
        const nameMatch = trader.store_name?.toLowerCase().includes(searchQuery);
        const descriptionMatch = trader.description?.toLowerCase().includes(searchQuery);
        const keywordMatch = trader.keywords?.some((keyword: string) =>
          String(keyword || '').toLowerCase().includes(searchQuery)
        );

        // Important: we fetch a wider candidate set to allow keyword-only matches,
        // but we must filter here to avoid returning unrelated businesses.
        if (!nameMatch && !descriptionMatch && !keywordMatch) {
          return;
        }

        let relevance = 0;
        let matchType: SearchResult['match_type'] = 'fulltext_match';

        if (nameMatch) {
          relevance = 1.0;
          matchType = 'exact_name';
        } else if (keywordMatch) {
          relevance = 0.9;
          matchType = 'keyword_match';
        } else if (descriptionMatch) {
          relevance = 0.8;
          matchType = 'exact_description';
        }

        if (location && !trader.location?.toLowerCase().includes(location.toLowerCase())) {
          return;
        }

        results.push({
          id: String(trader.id),
          store_name: trader.store_name,
          description: trader.description || '',
          location: trader.location || '',
          avatar_url: trader.avatar_url,
          business_type: 'trader',
          keywords: trader.keywords || [],
          slug: trader.slug || undefined,
          relevance_score: relevance,
          similarity_score: 0,
          match_type: matchType
        });
      });
    }

    // Search professionals - include verified OR mockup entries
    if (!businessType || businessType === 'professional') {
      const { data: professionals } = await supabase
        .from('professionals')
        .select('id, store_name, description, location, avatar_url, keywords, slug')
        .or('verified.eq.true,mockup.eq.true')
        .limit(scanLimitPerType);

      professionals?.forEach(pro => {
        const nameMatch = pro.store_name?.toLowerCase().includes(searchQuery);
        const descriptionMatch = pro.description?.toLowerCase().includes(searchQuery);
        const keywordMatch = pro.keywords?.some((keyword: string) =>
          String(keyword || '').toLowerCase().includes(searchQuery)
        );

        if (!nameMatch && !descriptionMatch && !keywordMatch) {
          return;
        }

        let relevance = 0;
        let matchType: SearchResult['match_type'] = 'fulltext_match';

        if (nameMatch) {
          relevance = 1.0;
          matchType = 'exact_name';
        } else if (keywordMatch) {
          relevance = 0.9;
          matchType = 'keyword_match';
        } else if (descriptionMatch) {
          relevance = 0.8;
          matchType = 'exact_description';
        }

        if (location && !pro.location?.toLowerCase().includes(location.toLowerCase())) {
          return;
        }

        results.push({
          id: String(pro.id),
          store_name: pro.store_name,
          description: pro.description || '',
          location: pro.location || '',
          avatar_url: pro.avatar_url,
          business_type: 'professional',
          keywords: pro.keywords || [],
          slug: pro.slug || undefined,
          relevance_score: relevance,
          similarity_score: 0,
          match_type: matchType
        });
      });
    }

    // Search crafties
    if (!businessType || businessType === 'craftie') {
      const { data: crafties } = await supabase
        .from('crafties')
        .select('id, store_name, description, location, avatar_url, keywords, slug')
        .or('verified.eq.true,mockup.eq.true')
        .limit(scanLimitPerType);

      crafties?.forEach(craftie => {
        const nameMatch = craftie.store_name?.toLowerCase().includes(searchQuery);
        const descriptionMatch = craftie.description?.toLowerCase().includes(searchQuery);
        const keywordMatch = craftie.keywords?.some((keyword: string) =>
          String(keyword || '').toLowerCase().includes(searchQuery)
        );

        if (!nameMatch && !descriptionMatch && !keywordMatch) {
          return;
        }

        let relevance = 0;
        let matchType: SearchResult['match_type'] = 'fulltext_match';

        if (nameMatch) {
          relevance = 1.0;
          matchType = 'exact_name';
        } else if (keywordMatch) {
          relevance = 0.9;
          matchType = 'keyword_match';
        } else if (descriptionMatch) {
          relevance = 0.8;
          matchType = 'exact_description';
        }

        if (location && !craftie.location?.toLowerCase().includes(location.toLowerCase())) {
          return;
        }

        results.push({
          id: String(craftie.id),
          store_name: craftie.store_name,
          description: craftie.description || '',
          location: craftie.location || '',
          avatar_url: craftie.avatar_url,
          business_type: 'craftie',
          keywords: craftie.keywords || [],
          slug: craftie.slug || undefined,
          relevance_score: relevance,
          similarity_score: 0,
          match_type: matchType
        });
      });
    }

    // Search hospitality
    if (!businessType || businessType === 'hospitality') {
      const { data: hospitality } = await supabase
        .from('hospitality')
        .select('id, store_name, description, location, avatar_url, keywords')
        .or('verified.eq.true,mockup.eq.true')
        .limit(scanLimitPerType);

      hospitality?.forEach(business => {
        const nameMatch = business.store_name?.toLowerCase().includes(searchQuery);
        const descriptionMatch = business.description?.toLowerCase().includes(searchQuery);
        const keywordMatch = business.keywords?.some((keyword: string) =>
          String(keyword || '').toLowerCase().includes(searchQuery)
        );

        if (!nameMatch && !descriptionMatch && !keywordMatch) {
          return;
        }

        let relevance = 0;
        let matchType: SearchResult['match_type'] = 'fulltext_match';

        if (nameMatch) {
          relevance = 1.0;
          matchType = 'exact_name';
        } else if (keywordMatch) {
          relevance = 0.9;
          matchType = 'keyword_match';
        } else if (descriptionMatch) {
          relevance = 0.8;
          matchType = 'exact_description';
        }

        if (location && !business.location?.toLowerCase().includes(location.toLowerCase())) {
          return;
        }

        results.push({
          id: String(business.id),
          store_name: business.store_name,
          description: business.description || '',
          location: business.location || '',
          avatar_url: business.avatar_url,
          business_type: 'hospitality',
          keywords: business.keywords || [],
          relevance_score: relevance,
          similarity_score: 0,
          match_type: matchType
        });
      });
    }

    results.sort((a, b) => b.relevance_score - a.relevance_score);
    const limitedResults = await hydrateCraftieSlugs(
      await hydrateTraderSlugs(results.slice(0, limit))
    );
    const searchTime = performance.now() - startTime;

    return {
      results: limitedResults,
      totalCount: limitedResults.length,
      hasExactMatches: limitedResults.some(r => ['exact_name', 'exact_description', 'keyword_match'].includes(r.match_type)),
      hasFuzzyMatches: false,
      searchTime
    };
  } catch (error) {
    console.error('❌ Fallback search error:', error);
    return {
      results: [],
      totalCount: 0,
      hasExactMatches: false,
      hasFuzzyMatches: false,
      searchTime: performance.now() - startTime
    };
  }
};

/**
 * Search businesses with fuzzy matching and fallback.
 *
 * The Supabase RPC function `search_businesses_fuzzy` still filters on
 * `verified = true` at the SQL layer. Our JS fallback search already
 * includes both `verified = true` and `mockup = true` entries.
 *
 * To ensure mockup (unclaimed) profiles are always visible/searchable,
 * we now:
 * - try the fuzzy RPC first (for best-quality matches),
 * - always run the JS fallback as well,
 * - merge both result sets, deduplicating by (business_type, id),
 * - sort by relevance_score and enforce the requested limit.
 */
export const searchBusinesses = async (params: SearchParams): Promise<SearchResponse> => {
  const startTime = performance.now();
  
  try {
    const {
      query,
      location = null,
      businessType = null,
      limit = 50,
      similarityThreshold = 0.3
    } = params;

    console.log('🔍 Searching businesses:', {
      query,
      location,
      businessType,
      limit,
      similarityThreshold
    });

    // 1) Try fuzzy search function first
    let fuzzyData: any[] = [];
    let fuzzyError: any = null;

    try {
      const { data, error } = await supabase.rpc('search_businesses_fuzzy', {
        search_query: query,
        search_location: location,
        business_type_filter: businessType,
        result_limit: limit,
        similarity_threshold: similarityThreshold
      });

      if (error) {
        fuzzyError = error;
        console.warn('⚠️ Fuzzy search RPC error, will rely more on fallback:', error.message);
      } else {
        fuzzyData = data || [];
      }
    } catch (rpcError: any) {
      fuzzyError = rpcError;
      console.warn('⚠️ Fuzzy search RPC threw, will rely more on fallback:', rpcError?.message || rpcError);
    }

    // Map fuzzy results to SearchResult shape (may be empty if RPC failed)
    const fuzzyResults: SearchResult[] = await hydrateCraftieSlugs(
      await hydrateTraderSlugs(
        (fuzzyData || []).map((r: any) => ({
          ...r,
          id: String(r.id), // Convert integer id to string
          similarity_score: r.similarity_score || 0,
          match_type: r.match_type || 'fulltext_match'
        }))
      )
    );

    // 2) Always run the JS fallback search as well (includes mockup=true)
    const fallbackResponse = await searchBusinessesFallback(params);
    const fallbackResults = fallbackResponse.results;

    // 3) Merge fuzzy + fallback, dedupe by (business_type, id)
    const resultMap = new Map<string, SearchResult>();

    const addResults = (items: SearchResult[]) => {
      for (const item of items) {
        const key = `${item.business_type}:${item.id}`;
        if (!resultMap.has(key)) {
          resultMap.set(key, item);
        }
      }
    };

    // Prefer fuzzy results when both exist for the same business
    addResults(fuzzyResults);
    addResults(fallbackResults);

    // 4) Sort by relevance and enforce limit
    let results = Array.from(resultMap.values());
    results.sort((a, b) => b.relevance_score - a.relevance_score);
    if (results.length > limit) {
      results = results.slice(0, limit);
    }

    const searchTime = performance.now() - startTime;

    // Analyze results
    const hasExactMatches = results.some((r: SearchResult) => 
      ['exact_name', 'exact_description', 'keyword_match'].includes(r.match_type)
    );
    const hasFuzzyMatches = results.some((r: SearchResult) => 
      r.match_type === 'fuzzy_match'
    );

    console.log('✅ Search completed:', {
      totalResults: results.length,
      hasExactMatches,
      hasFuzzyMatches,
      searchTime: `${searchTime.toFixed(2)}ms`,
      usedFuzzy: !fuzzyError,
    });

    // Log search analytics (non-blocking)
    logSearchAnalytics({
      query,
      location,
      businessType,
      resultsCount: results.length,
      hasFuzzyResults: hasFuzzyMatches,
      searchTime
    }).catch(err => {
      console.warn('⚠️ Failed to log search analytics:', err);
    });

    return {
      results,
      totalCount: results.length,
      hasExactMatches,
      hasFuzzyMatches,
      searchTime
    };

  } catch (error) {
    const searchTime = performance.now() - startTime;
    console.error('❌ Search service error:', error);
    
    return {
      results: [],
      totalCount: 0,
      hasExactMatches: false,
      hasFuzzyMatches: false,
      searchTime
    };
  }
};

/**
 * Get search suggestions based on popular queries
 */
export const getSearchSuggestions = async (partialQuery: string, limit: number = 5): Promise<string[]> => {
  try {
    if (partialQuery.length < 2) return [];

    // Get popular search terms that start with the partial query
    const { data, error } = await supabase
      .from('search_analytics')
      .select('search_query')
      .ilike('search_query', `${partialQuery}%`)
      .order('created_at', { ascending: false })
      .limit(limit * 3); // Get more to filter duplicates

    if (error) throw error;

    // Remove duplicates and limit results
    const suggestions = [...new Set(
      (data || [])
        .map(item => item.search_query)
        .filter(query => query.toLowerCase().startsWith(partialQuery.toLowerCase()))
    )].slice(0, limit);

    return suggestions;

  } catch (error) {
    console.error('❌ Failed to get search suggestions:', error);
    return [];
  }
};

/**
 * Get popular search terms
 */
export const getPopularSearches = async (limit: number = 10): Promise<Array<{query: string, count: number}>> => {
  try {
    const { data, error } = await supabase
      .from('search_analytics')
      .select('search_query')
      .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()) // Last 30 days
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Count occurrences
    const queryCounts: Record<string, number> = {};
    (data || []).forEach(item => {
      const query = item.search_query.toLowerCase().trim();
      if (query.length > 1) {
        queryCounts[query] = (queryCounts[query] || 0) + 1;
      }
    });

    // Sort by count and return top results
    const popular = Object.entries(queryCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, limit)
      .map(([query, count]) => ({ query, count }));

    return popular;

  } catch (error) {
    console.error('❌ Failed to get popular searches:', error);
    return [];
  }
};

/**
 * Log search analytics (internal function)
 */
const logSearchAnalytics = async (params: {
  query: string;
  location?: string | null;
  businessType?: string | null;
  resultsCount: number;
  hasFuzzyResults: boolean;
  searchTime: number;
}): Promise<void> => {
  try {
    // Get session info
    const sessionId = generateSessionId();
    const userAgent = navigator.userAgent;

    await supabase.rpc('log_search_query', {
      p_search_query: params.query,
      p_search_location: params.location,
      p_business_type_filter: params.businessType,
      p_results_count: params.resultsCount,
      p_has_fuzzy_results: params.hasFuzzyResults,
      p_session_id: sessionId,
      p_user_agent: userAgent
    });

  } catch (error) {
    // Don't throw - analytics should not break search
    console.warn('⚠️ Search analytics logging failed:', error);
  }
};

/**
 * Generate a session ID for analytics
 */
const generateSessionId = (): string => {
  // Try to get existing session ID from sessionStorage
  let sessionId = sessionStorage.getItem('search_session_id');
  
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('search_session_id', sessionId);
  }
  
  return sessionId;
};

/**
 * Search with autocomplete and suggestions
 */
export const searchWithSuggestions = async (
  query: string,
  options: Omit<SearchParams, 'query'> = {}
): Promise<SearchResponse & { suggestions: string[] }> => {
  // Get search results and suggestions in parallel
  const [searchResults, suggestions] = await Promise.all([
    searchBusinesses({ query, ...options }),
    getSearchSuggestions(query)
  ]);

  return {
    ...searchResults,
    suggestions
  };
};

/**
 * Validate search query
 */
export const validateSearchQuery = (query: string): { isValid: boolean; error?: string } => {
  if (!query || typeof query !== 'string') {
    return { isValid: false, error: 'Search query is required' };
  }

  const trimmed = query.trim();
  
  if (trimmed.length === 0) {
    return { isValid: false, error: 'Search query cannot be empty' };
  }

  if (trimmed.length > 100) {
    return { isValid: false, error: 'Search query is too long (max 100 characters)' };
  }

  // Check for potentially malicious content
  const suspiciousPatterns = [
    /[<>]/,           // HTML tags
    /javascript:/i,   // JavaScript URLs
    /on\w+\s*=/i,     // Event handlers
  ];

  if (suspiciousPatterns.some(pattern => pattern.test(trimmed))) {
    return { isValid: false, error: 'Invalid characters in search query' };
  }

  return { isValid: true };
};

/**
 * Format search results for display
 */
export const formatSearchResults = (results: SearchResult[]): SearchResult[] => {
  return results.map(result => ({
    ...result,
    // Ensure avatar_url has a fallback
    avatar_url: result.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${result.store_name}`,
    // Truncate long descriptions
    description: result.description.length > 150 
      ? result.description.substring(0, 147) + '...'
      : result.description,
    // Ensure keywords is always an array
    keywords: Array.isArray(result.keywords) ? result.keywords : []
  }));
};

/**
 * Get search result statistics
 */
export const getSearchStats = (results: SearchResult[]): {
  byBusinessType: Record<string, number>;
  byMatchType: Record<string, number>;
  avgRelevanceScore: number;
  avgSimilarityScore: number;
} => {
  const byBusinessType: Record<string, number> = {};
  const byMatchType: Record<string, number> = {};
  let totalRelevance = 0;
  let totalSimilarity = 0;

  results.forEach(result => {
    // Count by business type
    byBusinessType[result.business_type] = (byBusinessType[result.business_type] || 0) + 1;
    
    // Count by match type
    byMatchType[result.match_type] = (byMatchType[result.match_type] || 0) + 1;
    
    // Sum scores
    totalRelevance += result.relevance_score;
    totalSimilarity += result.similarity_score;
  });

  return {
    byBusinessType,
    byMatchType,
    avgRelevanceScore: results.length > 0 ? totalRelevance / results.length : 0,
    avgSimilarityScore: results.length > 0 ? totalSimilarity / results.length : 0
  };
};

export default {
  searchBusinesses,
  searchWithSuggestions,
  getSearchSuggestions,
  getPopularSearches,
  validateSearchQuery,
  formatSearchResults,
  getSearchStats
};
